import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Typography,
  Box,
  Card,
  CardContent,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  Snackbar,
  Alert,
  Avatar,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Checkbox,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  List as ListIcon,
  Search as SearchIcon,
  WhatsApp as WhatsAppIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { postData, updateData, fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';

const EnquiryModal = ({
  open,
  onClose,
  onSave,
  enquiryData = null,
  mode = 'create',
  customers = [],
  items = []
}) => {

  const { companyId, branchId, token, userId } = useAuth();

  const [loading, setLoading] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState(null);

  const [formData, setFormData] = useState({
    BillNumber: '',
    CustomerId: '',
    CustomerName: '',
    ContactNumber: '',
    EnquiryDate: '',
    FinYear: new Date().getFullYear().toString(),
    Remarks: '',
    TotalAmount: '',
    items: []
  });

  const [errors, setErrors] = useState({});
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showWhatsAppDialog, setShowWhatsAppDialog] = useState(false);
  const [savedEnquiryId, setSavedEnquiryId] = useState(null);
  const [isBlacklisted, setIsBlacklisted] = useState(false);

  // Get Items modal state
  const [getItemsModalOpen, setGetItemsModalOpen] = useState(false);
  const [getItemsLoading, setGetItemsLoading] = useState(false);
  const [addingItemsLoading, setAddingItemsLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [getItemsSearchValue, setGetItemsSearchValue] = useState('');
  const [currentItemsPage, setCurrentItemsPage] = useState(1);
  const [itemsPerPage] = useState(50);

  const fetchBillNumberForEnquiry = async () => {
    if (mode !== 'create') return;

    try {
      const response = await fetchData('ProductEnquiryApi', `/MaxBillno/${companyId}/${branchId}`, token);
      const data = response.response_data || response || {};
      const billNumber = data.BillNumber || data.billNumber || 1;
      const finYear = data.FinYear || data.finYear || new Date().getFullYear().toString();

      setFormData(prev => ({
        ...prev,
        BillNumber: billNumber,
        FinYear: finYear
      }));
    } catch (error) {
      console.error('Error fetching bill number for enquiry:', error);
    }
  };

  useEffect(() => {
    if (open) {
      if (enquiryData && mode !== 'create') {
        // Handle both direct enquiry data and nested structure
        const enquiry = enquiryData.ProductEnquiry || enquiryData;
        const details = enquiryData.ProductEnquiryDetails || enquiryData.EnquiryDetails || [];
        
        const mappedItems = details.map(item => ({
          ItemId: item.ItemId || '',
          ItemName: item.ItemName || '',
          Photo: item.Photo || items.find(masterItem => String(masterItem.Id) === String(item.ItemId))?.Photo || '',
          Quantity: item.Quantity?.toString() || '1',
          Days: item.Days?.toString() || '1',
          Rate: item.Rate?.toString() || '',
          Amount: item.Amount?.toString() || ''
        }));
        
        // Calculate total amount from items
        const totalAmount = mappedItems.reduce((sum, item) => sum + parseFloat(item.Amount || 0), 0);
        
        setFormData({
          Id: enquiry.Id || '',
          BillNumber: enquiry.BillNumber || '',
          CustomerId: enquiry.CustomerId || '',
          CustomerName: enquiry.CustomerName || '',
          ContactNumber: enquiry.ContactNumber || '',
          EnquiryDate: enquiry.EnquiryDate ? enquiry.EnquiryDate.split('T')[0] : '',
          FinYear: enquiry.FinYear || new Date().getFullYear().toString(),
          Remarks: enquiry.Remarks || '',
          TotalAmount: totalAmount.toFixed(2),
          items: mappedItems
        });
      } else {
        const today = new Date().toISOString().split('T')[0];

        setFormData({
          BillNumber: '',
          CustomerId: '',
          CustomerName: '',
          ContactNumber: '',
          EnquiryDate: today,
          FinYear: new Date().getFullYear().toString(),
          Remarks: '',
          TotalAmount: '',
          items: []
        });
      }
      setErrors({});
      setShowNewCustomerForm(false);
      setShowSuccessMessage(false);
      setSuccessMessage('');
      setIsBlacklisted(false);

      if (mode === 'create') {
        fetchBillNumberForEnquiry();
      }
    }
  }, [open, enquiryData, mode]);

  useEffect(() => {
    if (!getItemsModalOpen || !getItemsLoading) return undefined;
    if (items.length > 0) {
      setGetItemsLoading(false);
      return undefined;
    }
    const timeout = setTimeout(() => setGetItemsLoading(false), 10000);
    return () => clearTimeout(timeout);
  }, [getItemsModalOpen, getItemsLoading, items.length]);

  const handleInputChange = (field, value, itemIndex = null) => {
    if (itemIndex !== null) {
      setFormData(prev => {
        const newItems = [...prev.items];
        newItems[itemIndex] = {
          ...newItems[itemIndex],
          [field]: value
        };
        return {
          ...prev,
          items: newItems
        };
      });

      if (field === 'ItemId') {
        const isDuplicate = formData.items.some((item, index) =>
          index !== itemIndex && item.ItemId === value && value !== ''
        );

        if (isDuplicate) {
          setErrors(prev => ({
            ...prev,
            [`ItemId_${itemIndex}`]: 'This item is already selected.'
          }));
          return;
        } else {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[`ItemId_${itemIndex}`];
            return newErrors;
          });
        }

        const selectedItem = items.find(item => item.Id === value);
        if (selectedItem) {
          setFormData(prev => {
            const newItems = [...prev.items];
            newItems[itemIndex] = {
              ...newItems[itemIndex],
              ItemName: selectedItem.Name || '',
              Photo: selectedItem.Photo || '',
              Rate: selectedItem.EventSaleRate || selectedItem.Rate || '',
              Quantity: '1',
              Days: '1'
            };
            return {
              ...prev,
              items: newItems
            };
          });

          setTimeout(() => {
            calculateItemAmounts(itemIndex, 'Quantity', '1');
          }, 0);
        }
      }

      if (field === 'Quantity' || field === 'Days' || field === 'Rate') {
        calculateItemAmounts(itemIndex, field, value);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));

      if (errors[field]) {
        setErrors(prev => ({
          ...prev,
          [field]: ''
        }));
      }

      if (field === 'CustomerId') {
        const selectedCustomer = customers.find(customer => customer.Id === value);
        if (selectedCustomer) {
          // Check if customer is blacklisted
          setIsBlacklisted(selectedCustomer.Blacklist === true || selectedCustomer.Blacklist === 1);

          setFormData(prev => ({
            ...prev,
            CustomerName: selectedCustomer.Name || '',
            ContactNumber: selectedCustomer.ContactNumber1 || selectedCustomer.ContactNumber || ''
          }));
        } else {
          setIsBlacklisted(false);
        }
      }
      
      // Clear CustomerName error when user types or selects
      if (field === 'CustomerName' || field === 'CustomerId') {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.CustomerName;
          return newErrors;
        });
      }
      
      // Clear ContactNumber error when user types
      if (field === 'ContactNumber') {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.ContactNumber;
          return newErrors;
        });
      }
    }
  };

  const calculateItemAmounts = (itemIndex, changedField, changedValue) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      const item = newItems[itemIndex];

      const quantity = changedField === 'Quantity' ? parseFloat(changedValue) || 0 : parseFloat(item.Quantity) || 0;
      const days = changedField === 'Days' ? parseFloat(changedValue) || 0 : parseFloat(item.Days) || 0;
      const rate = changedField === 'Rate' ? parseFloat(changedValue) || 0 : parseFloat(item.Rate) || 0;

      const amount = quantity * days * rate;

      newItems[itemIndex] = {
        ...item,
        Amount: amount.toFixed(2)
      };

      const totalAmount = newItems.reduce((sum, item) => sum + parseFloat(item.Amount || 0), 0);

      return {
        ...prev,
        items: newItems,
        TotalAmount: totalAmount.toFixed(2)
      };
    });
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          ItemId: '',
          ItemName: '',
          Quantity: '1',
          Days: '1',
          Rate: '',
          Amount: ''
        }
      ]
    }));
  };

  const removeItem = (itemIndex) => {
    setFormData(prev => {
      const newItems = prev.items.filter((_, index) => index !== itemIndex);
      const totalAmount = newItems.reduce((sum, item) => sum + parseFloat(item.Amount || 0), 0);

      return {
        ...prev,
        items: newItems,
        TotalAmount: totalAmount.toFixed(2)
      };
    });
  };

  const validateForm = () => {
    const newErrors = {};

    // Customer validation - either CustomerId or CustomerName must be provided
    if (!formData.CustomerId && !formData.CustomerName) {
      newErrors.CustomerName = 'Customer is required';
    }
    
    // Contact number validation - must be exactly 10 digits
    if (formData.ContactNumber) {
      const cleanedNumber = formData.ContactNumber.replace(/[^0-9]/g, '');
      if (cleanedNumber.length !== 10) {
        newErrors.ContactNumber = 'Contact number must be exactly 10 digits';
      }
    }
    
    if (!formData.EnquiryDate) newErrors.EnquiryDate = 'Enquiry date is required';
    
    // Check if at least one item is added
    if (!formData.items || formData.items.length === 0) {
      newErrors.items = 'At least one item is required';
    } else {
      // Item validation
      formData.items.forEach((item, index) => {
        if (!item.ItemId) newErrors[`ItemId_${index}`] = `Item ${index + 1} is required`;
        if (!item.Quantity || item.Quantity <= 0) {
          newErrors[`Quantity_${index}`] = `Valid quantity for item ${index + 1} is required`;
        }
        if (!item.Days || item.Days <= 0) {
          newErrors[`Days_${index}`] = `Valid days for item ${index + 1} is required`;
        }
        if (!item.Rate || item.Rate <= 0) {
          newErrors[`Rate_${index}`] = `Valid rate for item ${index + 1} is required`;
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const enquiryData = {
        BillNumber: formData.BillNumber,
        CustomerId: formData.CustomerId || null,
        CustomerName: formData.CustomerName,
        ContactNumber: formData.ContactNumber,
        EnquiryDate: formData.EnquiryDate,
        FinYear: formData.FinYear,
        Remarks: formData.Remarks,
        CompanyId: companyId,
        BranchId: branchId,
        CreatedBy: userId,
        ModifiedBy: userId,
        IsActive: true
      };

      const enquiryDetails = formData.items.map(item => ({
        ItemId: item.ItemId,
        ItemName: item.ItemName,
        Quantity: parseFloat(item.Quantity) || 0,
        Rate: parseFloat(item.Rate) || 0,
        Days: parseFloat(item.Days) || 0,
        Amount: parseFloat(item.Amount) || 0,
        IsActive: true
      }));

      const dataToSend = {
        ProductEnquiry: enquiryData,
        ProductEnquiryDetails: enquiryDetails
      };

      let response;
      if (mode === 'edit' && formData.Id) {
        dataToSend.ProductEnquiry.Id = formData.Id;
        response = await updateData('ProductEnquiryApi', formData.Id, dataToSend, token);
      } else {
        response = await postData('ProductEnquiryApi', dataToSend, token);
      }

      if (response && response.responseCode === '1111') {
        const message = mode === 'edit' ? 'Enquiry updated successfully' : 'Enquiry created successfully';
        setSuccessMessage(message);
        setShowSuccessMessage(true);

        // Store the enquiry ID for WhatsApp sending
        const enquiryId = mode === 'edit' ? formData.Id : response.kotId || response.kotId;
        setSavedEnquiryId(enquiryId);

        // Show WhatsApp confirmation dialog after a brief delay
        setTimeout(() => {
          setShowSuccessMessage(false);
          setShowWhatsAppDialog(true);
        }, 1500);
      } else {
        setSuccessMessage('Failed to save enquiry. Please try again.');
        setShowSuccessMessage(true);
      }
    } catch (error) {
      console.error('Error saving enquiry:', error);
      setSuccessMessage('Error occurred while saving enquiry. Please try again.');
      setShowSuccessMessage(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle Get Items modal
  const handleOpenGetItemsModal = async () => {
    setGetItemsModalOpen(true);
    setGetItemsLoading(true);
    setSelectedItems([]);
    setGetItemsSearchValue('');
    setCurrentItemsPage(1);

    if (items.length > 0) setGetItemsLoading(false);
  };

  const handleCloseGetItemsModal = () => {
    setGetItemsModalOpen(false);
    setSelectedItems([]);
    setGetItemsSearchValue('');
  };

  // Handle item selection in Get Items modal
  const handleItemSelection = (item, isSelected) => {
    if (isSelected) {
      const newSelectedItem = {
        ...item,
        selectedQuantity: 1,
        selectedDays: 1,
        selectedRate: item.EventSaleRate || item.Rate || 0
      };
      setSelectedItems(prev => [...prev, newSelectedItem]);
    } else {
      setSelectedItems(prev => prev.filter(selectedItem => selectedItem.Id !== item.Id));
    }
  };

  // Update selected item details
  const handleSelectedItemChange = (itemId, field, value) => {
    setSelectedItems(prev => prev.map(item =>
      item.Id === itemId ? { ...item, [field]: value } : item
    ));
  };

  // Handle search in Get Items modal
  const handleGetItemsSearch = (searchValue) => {
    setGetItemsSearchValue(searchValue);
    setCurrentItemsPage(1);
  };

  // Filter items based on search value for Get Items modal
  const getFilteredItems = () => {
    if (!getItemsSearchValue.trim()) {
      return items;
    }

    const searchTerm = getItemsSearchValue.toLowerCase().trim();
    return items.filter(item =>
      item.Name?.toLowerCase().includes(searchTerm) ||
      item.HSNCode?.toLowerCase().includes(searchTerm) ||
      item.Code?.toLowerCase().includes(searchTerm)
    );
  };

  // Get paginated items for Get Items modal
  const getPaginatedItems = () => {
    const filtered = getFilteredItems();
    const startIndex = (currentItemsPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getTotalPages = Math.ceil(getFilteredItems().length / itemsPerPage);

  // Check if item is already in enquiry items
  const isItemAlreadyInEnquiry = (itemId) => {
    return formData.items.some(item => item.ItemId === parseInt(itemId));
  };

  // Add selected items to enquiry items
  const handleAddSelectedItems = async () => {
    if (selectedItems.length === 0) return;

    setAddingItemsLoading(true);

    try {
      const itemsToAdd = selectedItems.filter(
        selectedItem => !isItemAlreadyInEnquiry(selectedItem.Id)
      );

      if (itemsToAdd.length === 0) {
        handleCloseGetItemsModal();
        return;
      }

      const newItems = itemsToAdd.map(selectedItem => {
        const quantity = parseFloat(selectedItem.selectedQuantity) || 1;
        const days = parseFloat(selectedItem.selectedDays) || 1;
        const rate = parseFloat(selectedItem.selectedRate) || 0;
        const amount = quantity * days * rate;

        return {
          ItemId: selectedItem.Id,
          ItemName: selectedItem.Name,
          Photo: selectedItem.Photo || '',
          Quantity: quantity.toString(),
          Days: days.toString(),
          Rate: rate.toString(),
          Amount: amount.toFixed(2)
        };
      });

      const updatedItems = [...formData.items, ...newItems];
      const totalAmount = updatedItems.reduce((sum, item) => sum + parseFloat(item.Amount || 0), 0);

      setFormData(prev => ({
        ...prev,
        items: updatedItems,
        TotalAmount: totalAmount.toFixed(2)
      }));

      handleCloseGetItemsModal();

    } catch (error) {
      console.error('Error adding selected items:', error);
      setSuccessMessage('Failed to add selected items. Please try again.');
      setShowSuccessMessage(true);
    } finally {
      setAddingItemsLoading(false);
    }
  };

  const handleSendToWhatsApp = async () => {
    try {
      setShowWhatsAppDialog(false);
      
      // Fetch full enquiry details
      const response = await fetchData('ProductEnquiryApi', `/${savedEnquiryId}`, token);
      const fullEnquiry = response.response_data;
      
      if (!fullEnquiry) {
        console.error('Failed to fetch enquiry details');
        onSave();
        onClose();
        return;
      }
      
      // Format message for WhatsApp
      let message = `*Product Enquiry*\n\n`;
      message += `Bill No: ${fullEnquiry.ProductEnquiry.BillNumber}\n`;
      message += `Customer: ${fullEnquiry.ProductEnquiry.CustomerName}\n`;
      message += `Date: ${new Date(fullEnquiry.ProductEnquiry.EnquiryDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}\n\n`;
      message += `*Items:*\n`;
      
      if (fullEnquiry.ProductEnquiryDetails && fullEnquiry.ProductEnquiryDetails.length > 0) {
        fullEnquiry.ProductEnquiryDetails.forEach((item, index) => {
          message += `\n${index + 1}. ${item.ItemName}\n`;
          message += `   Quantity: ${item.Quantity}\n`;
          message += `   Days: ${item.Days}\n`;
          message += `   Rate: ₹${parseFloat(item.Rate).toLocaleString('en-IN')}\n`;
          message += `   Amount: ₹${parseFloat(item.Amount).toLocaleString('en-IN')}\n`;
        });
        
        const totalAmount = fullEnquiry.ProductEnquiryDetails.reduce((sum, item) => sum + parseFloat(item.Amount || 0), 0);
        message += `\n*Total Amount: ₹${totalAmount.toLocaleString('en-IN')}*`;
      }
      
      if (fullEnquiry.ProductEnquiry.Remarks) {
        message += `\n\n*Remarks:* ${fullEnquiry.ProductEnquiry.Remarks}`;
      }
      
      // Encode message for URL
      const encodedMessage = encodeURIComponent(message);
      const phoneNumber = fullEnquiry.ProductEnquiry.ContactNumber?.replace(/[^0-9]/g, '');
      
      // Open WhatsApp with pre-filled message
      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
      window.open(whatsappUrl, '_blank');
      
      onSave();
      onClose();
    } catch (error) {
      console.error('Error sending WhatsApp message:', error);
      onSave();
      onClose();
    }
  };

  const handleSkipWhatsApp = () => {
    setShowWhatsAppDialog(false);
    onSave();
    onClose();
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{
        background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 0.75,
        px: 2
      }}>
        <Typography variant="h6" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
          {mode === 'create' ? 'New Enquiry' : mode === 'edit' ? 'Edit Enquiry' : 'View Enquiry'}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'white', p: 0.5 }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 1.5 }}>
        <Grid container spacing={1.5}>
          {/* Blacklist Warning */}
          {isBlacklisted && (
            <Grid item xs={12}>
              <Alert
                severity="error"
                sx={{
                  fontSize: '0.75rem',
                  py: 0.5,
                  '& .MuiAlert-icon': {
                    fontSize: 18
                  },
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                  '@keyframes pulse': {
                    '0%, 100%': {
                      opacity: 1,
                    },
                    '50%': {
                      opacity: 0.8,
                    },
                  }
                }}
              >
                ⚠️ BLACKLISTED CUSTOMER - Proceed with caution!
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <Paper elevation={0} sx={{ 
              p: 1, 
              border: isBlacklisted ? '2px solid #ef4444' : '1px solid #e2e8f0', 
              borderRadius: 2,
              bgcolor: isBlacklisted ? '#fef2f2' : 'white',
              boxShadow: isBlacklisted ? '0 4px 12px rgba(239, 68, 68, 0.2)' : 'none',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: isBlacklisted ? '0 6px 16px rgba(239, 68, 68, 0.3)' : 'none'
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar sx={{ 
                  bgcolor: isBlacklisted ? '#ef4444' : "#0d766e", 
                  mr: 0.75, 
                  width: 24, 
                  height: 24 
                }}>
                  {isBlacklisted ? (
                    <WarningIcon sx={{ fontSize: 14 }} />
                  ) : (
                    <PersonIcon sx={{ fontSize: 14 }} />
                  )}
                </Avatar>
                <Typography variant="subtitle1" sx={{ 
                  fontWeight: 600, 
                  color: isBlacklisted ? '#dc2626' : '#2d3748', 
                  fontSize: '0.75rem' 
                }}>
                  Customer Details {isBlacklisted && '⚠️'}
                </Typography>
              </Box>
              <Grid container spacing={1}>
                <Grid item xs={12} md={2} className="hide-on-mobile">
                  <TextField
                    fullWidth
                    size="small"
                    label="Bill No"
                    value={formData.BillNumber}
                    disabled
                    sx={{
                      '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                      '& .MuiInputBase-input': { fontSize: '0.7rem', py: 0.5 },
                      '& .MuiInputBase-root': { height: '32px', bgcolor: '#f8fafc' }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2.5} className="hide-on-mobile">
                  <TextField
                    fullWidth
                    size="small"
                    label="Enquiry Date *"
                    type="date"
                    value={formData.EnquiryDate}
                    onChange={(e) => handleInputChange('EnquiryDate', e.target.value)}
                    disabled={mode === 'view'}
                    error={!!errors.EnquiryDate}
                    helperText={errors.EnquiryDate}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                      '& .MuiInputBase-input': { fontSize: '0.7rem', py: 0.5 },
                      '& .MuiInputBase-root': { height: '32px' }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <Autocomplete
                    freeSolo
                    options={customers}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option;
                      const phone = option.ContactNumber1 || option.ContactNumber || '';
                      return `${option.Name} - ${phone}`;
                    }}
                    value={customers.find(c => c.Id === formData.CustomerId) || formData.CustomerName || null}
                    onChange={(event, newValue) => {
                      if (typeof newValue === 'string') {
                        handleInputChange('CustomerName', newValue);
                        handleInputChange('CustomerId', '');
                      } else if (newValue) {
                        handleInputChange('CustomerId', newValue.Id);
                      } else {
                        handleInputChange('CustomerId', '');
                        handleInputChange('CustomerName', '');
                      }
                    }}
                    onInputChange={(event, newInputValue) => {
                      handleInputChange('CustomerName', newInputValue);
                    }}
                    disabled={mode === 'view' || mode === 'edit'}
                    componentsProps={{
                      popper: {
                        sx: {
                          '& .MuiAutocomplete-listbox': {
                            fontSize: '0.7rem',
                            '& .MuiAutocomplete-option': {
                              fontSize: '0.7rem',
                              minHeight: '32px',
                              py: 0.5
                            }
                          }
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search Customer *"
                        placeholder="Type or select customer"
                        error={!!errors.CustomerName}
                        helperText={errors.CustomerName}
                        size="small"
                        sx={{
                          '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                          '& .MuiInputBase-input': { fontSize: '0.7rem', py: 0.5 },
                          '& .MuiInputBase-root': { height: '32px', bgcolor: mode === 'view' || mode === 'edit' ? '#f8fafc' : 'white' }
                        }}
                      />
                    )}
                  />
                  {mode === 'create' && (
                    <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                      <Button
                        size="small"
                        onClick={() => setShowNewCustomerForm(true)}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.7rem',
                          color: '#059669',
                          fontWeight: 600,
                          p: 0,
                          minWidth: 'auto',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        + Create New Customer
                      </Button>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} md={2.5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Contact Number"
                    value={formData.ContactNumber}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow only numbers and limit to 10 digits
                      const cleanedValue = value.replace(/[^0-9]/g, '').slice(0, 10);
                      handleInputChange('ContactNumber', cleanedValue);
                    }}
                    disabled={mode === 'view'}
                    error={!!errors.ContactNumber}
                    helperText={errors.ContactNumber}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon sx={{ fontSize: 14, color: '#64748b' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                      '& .MuiInputBase-input': { fontSize: '0.7rem', py: 0.5 },
                      '& .MuiInputBase-root': { height: '32px' },
                      '& .MuiFormHelperText-root': { fontSize: '0.65rem' }
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 1, border: '1px solid #e2e8f0', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2d3748', fontSize: '0.75rem' }}>
                    Items
                  </Typography>
                  {errors.items && (
                    <Typography variant="caption" sx={{ color: '#d32f2f', fontSize: '0.65rem' }}>
                      {errors.items}
                    </Typography>
                  )}
                </Box>
                {mode !== 'view' && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ListIcon sx={{ fontSize: 14 }} />}
                    onClick={handleOpenGetItemsModal}
                    sx={{
                      borderColor: '#1e40af',
                      color: '#1e40af',
                      '&:hover': {
                        borderColor: '#1d4ed8',
                        backgroundColor: '#eff6ff'
                      },
                      textTransform: 'none',
                      fontSize: '0.7rem',
                      py: 0.25,
                      px: 1
                    }}
                  >
                    Get Items
                  </Button>
                )}
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, py: 0.5, width: '45%' }}>Item Name</TableCell>
                      <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, py: 0.5, width: '8%' }}>Qty</TableCell>
                      <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, py: 0.5, width: '8%' }}>Days</TableCell>
                      <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, py: 0.5, width: '12%' }}>Rate</TableCell>
                      <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, py: 0.5, width: '17%' }}>Amount</TableCell>
                      {mode !== 'view' && <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, py: 0.5, width: '10%', textAlign: 'right' }}>Action</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ py: 0.5, fontSize: '0.7rem', fontWeight: 500 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {item.Photo ? <img src={item.Photo} alt={item.ItemName || 'Item'} title="Click to enlarge" onClick={() => setPreviewPhoto(item.Photo)} style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, border: '1px solid #e5e7eb', cursor: 'zoom-in' }} /> : <Box sx={{ width: 36, height: 36, display: 'grid', placeItems: 'center', bgcolor: '#f3f4f6', borderRadius: 1 }}>📦</Box>}
                            <span>{item.ItemName || '-'}</span>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={item.Quantity}
                            onChange={(e) => handleInputChange('Quantity', e.target.value, index)}
                            disabled={mode === 'view'}
                            error={!!errors[`Quantity_${index}`]}
                            sx={{ 
                              width: 60, 
                              '& .MuiInputBase-input': { fontSize: '0.7rem', py: 0.25 },
                              '& .MuiInputBase-root': { height: '28px' }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={item.Days}
                            onChange={(e) => handleInputChange('Days', e.target.value, index)}
                            disabled={mode === 'view'}
                            error={!!errors[`Days_${index}`]}
                            sx={{ 
                              width: 60, 
                              '& .MuiInputBase-input': { fontSize: '0.7rem', py: 0.25 },
                              '& .MuiInputBase-root': { height: '28px' }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={item.Rate}
                            onChange={(e) => handleInputChange('Rate', e.target.value, index)}
                            disabled={mode === 'view'}
                            error={!!errors[`Rate_${index}`]}
                            sx={{ 
                              width: 80, 
                              '& .MuiInputBase-input': { fontSize: '0.7rem', py: 0.25 },
                              '& .MuiInputBase-root': { height: '28px' }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>
                          ₹{parseFloat(item.Amount || 0).toLocaleString('en-IN')}
                        </TableCell>
                        {mode !== 'view' && (
                          <TableCell sx={{ py: 0.5, textAlign: 'right' }}>
                            <IconButton
                              size="small"
                              onClick={() => removeItem(index)}
                              color="error"
                              sx={{ p: 0.25 }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                <Card sx={{ minWidth: 180, bgcolor: '#f8fafc' }}>
                  <CardContent sx={{ p: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#2d3748' }}>
                        Total Amount:
                      </Typography>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: "#0d766e" }}>
                        ₹{parseFloat(formData.TotalAmount || 0).toLocaleString('en-IN')}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Remarks"
              multiline
              rows={2}
              value={formData.Remarks}
              onChange={(e) => handleInputChange('Remarks', e.target.value)}
              disabled={mode === 'view'}
              sx={{
                '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                '& .MuiInputBase-input': { fontSize: '0.7rem', py: 0.5 }
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 1.5, borderTop: '1px solid #e2e8f0' }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.5, px: 1.5 }}>
          Cancel
        </Button>
        {mode !== 'view' && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading}
            sx={{
              textTransform: 'none',
              background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
              fontSize: '0.75rem',
              py: 0.5,
              px: 1.5
            }}
          >
            {loading ? 'Saving...' : mode === 'edit' ? 'Update' : 'Create'}
          </Button>
        )}
      </DialogActions>

      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={3000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={successMessage.includes('successfully') ? 'success' : 'error'}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Get Items Modal */}
      <Dialog
        open={getItemsModalOpen}
        onClose={handleCloseGetItemsModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '6px',
            m: 0.5,
            maxHeight: '75vh',
            width: '85%',
            maxWidth: '1000px'
          }
        }}
      >
        <DialogTitle sx={{
          py: 0.5,
          px: 1.5,
          borderBottom: '1px solid #d1d5db',
          bgcolor: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          color: '#fff'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ListIcon sx={{ color: '#fff', fontSize: 16 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>
                Get Items ({getFilteredItems().length} available)
              </Typography>
            </Box>
            <IconButton onClick={handleCloseGetItemsModal} size="small" sx={{ color: '#fff', p: 0.25 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 1.5, bgcolor: '#f8fafc' }}>
          {getItemsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '150px' }}>
              <CircularProgress size={20} />
              <Typography variant="body2" sx={{ ml: 1.5, color: '#6b7280', fontSize: '0.8rem' }}>Loading items...</Typography>
            </Box>
          ) : (
            <>
              {/* Search */}
              <Box sx={{ mb: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search items by name, HSN code, or item code..."
                  value={getItemsSearchValue}
                  onChange={(e) => handleGetItemsSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 14, color: '#6b7280' }} />
                      </InputAdornment>
                    ),
                    endAdornment: getItemsSearchValue && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => handleGetItemsSearch('')}
                          sx={{ p: 0.25 }}
                        >
                          <CloseIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' },
                    '& .MuiOutlinedInput-root': { bgcolor: '#fff' }
                  }}
                />
              </Box>

              {/* Selected Items Summary */}
              {selectedItems.length > 0 && (
                <Box sx={{ mb: 1.5, p: 1, bgcolor: '#eff6ff', borderRadius: 1, border: '1px solid #bfdbfe' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 0.5, display: 'block' }}>
                    Selected Items ({selectedItems.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedItems.slice(0, 8).map(item => (
                      <Chip
                        key={item.Id}
                        label={`${item.Name} (${item.selectedQuantity})`}
                        size="small"
                        onDelete={() => handleItemSelection(item, false)}
                        sx={{ fontSize: '0.7rem', height: '20px' }}
                      />
                    ))}
                    {selectedItems.length > 8 && (
                      <Chip label={`+${selectedItems.length - 8} more`} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: '20px' }} />
                    )}
                  </Box>
                </Box>
              )}

              {/* Items Table */}
              <Box sx={{ border: '1px solid #d1d5db', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', height: '28px' }}>
                        <th style={{ padding: '3px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '5%' }}>
                          <Checkbox
                            size="small"
                            checked={selectedItems.length === getPaginatedItems().length && getPaginatedItems().length > 0}
                            indeterminate={selectedItems.length > 0 && selectedItems.length < getPaginatedItems().length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                getPaginatedItems().forEach(item => {
                                  if (!selectedItems.find(si => si.Id === item.Id)) {
                                    handleItemSelection(item, true);
                                  }
                                });
                              } else {
                                getPaginatedItems().forEach(item => {
                                  handleItemSelection(item, false);
                                });
                              }
                            }}
                            sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' } }}
                          />
                        </th>
                        <th style={{ padding: '3px 6px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '35%', fontSize: '0.65rem' }}>Item</th>
                        <th style={{ padding: '3px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '10%', fontSize: '0.65rem' }}>HSN</th>
                        <th style={{ padding: '3px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '10%', fontSize: '0.65rem' }}>Qty</th>
                        <th style={{ padding: '3px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '10%', fontSize: '0.65rem' }}>Days</th>
                        <th style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '15%', fontSize: '0.65rem' }}>Rate</th>
                        <th style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '15%', fontSize: '0.65rem' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPaginatedItems().map((item, index) => {
                        const selectedItem = selectedItems.find(si => si.Id === item.Id);
                        const isSelected = !!selectedItem;
                        const isAlreadyAdded = isItemAlreadyInEnquiry(item.Id);

                        return (
                          <tr key={item.Id} style={{
                            borderBottom: '1px solid #e5e7eb',
                            background: isSelected ? '#eff6ff' : (index % 2 === 0 ? '#fff' : '#f9fafb'),
                            opacity: isAlreadyAdded ? 0.5 : 1,
                            height: '30px'
                          }}>
                            <td style={{ padding: '3px', textAlign: 'center' }}>
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                disabled={isAlreadyAdded}
                                onChange={(e) => handleItemSelection(item, e.target.checked)}
                              />
                            </td>
                            <td style={{ padding: '3px 6px', fontWeight: 500, fontSize: '0.7rem' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {item.Photo ? <img src={item.Photo} alt={item.Name || 'Item'} onClick={() => setPreviewPhoto(item.Photo)} style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 4, border: '1px solid #e5e7eb', flexShrink: 0, cursor: 'zoom-in' }} /> : <Box sx={{ width: 28, height: 28, display: 'grid', placeItems: 'center', bgcolor: '#f3f4f6', borderRadius: 1, flexShrink: 0 }}>📦</Box>}
                                <span>{item.Name}</span>
                                {isAlreadyAdded && (
                                  <Chip label="Added" size="small" color="success" sx={{ fontSize: '0.55rem', height: 12 }} />
                                )}
                              </Box>
                            </td>
                            <td style={{ padding: '3px 4px', textAlign: 'center', fontSize: '0.65rem', color: '#6b7280' }}>{item.HSNCode || '-'}</td>
                            <td style={{ padding: '3px 4px', textAlign: 'center' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedQuantity}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedQuantity', e.target.value)}
                                  inputProps={{ min: 1 }}
                                  sx={{
                                    width: 50,
                                    '& .MuiInputBase-input': {
                                      py: 0.15,
                                      px: 0.15,
                                      fontSize: '0.65rem',
                                      textAlign: 'center'
                                    }
                                  }}
                                />
                              ) : (
                                <span style={{ color: '#9ca3af', fontSize: '0.65rem' }}>-</span>
                              )}
                            </td>
                            <td style={{ padding: '3px 4px', textAlign: 'center' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedDays}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedDays', e.target.value)}
                                  inputProps={{ min: 1 }}
                                  sx={{
                                    width: 50,
                                    '& .MuiInputBase-input': {
                                      py: 0.15,
                                      px: 0.15,
                                      fontSize: '0.65rem',
                                      textAlign: 'center'
                                    }
                                  }}
                                />
                              ) : (
                                <span style={{ color: '#9ca3af', fontSize: '0.65rem' }}>-</span>
                              )}
                            </td>
                            <td style={{ padding: '3px 4px', textAlign: 'right' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedRate}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedRate', e.target.value)}
                                  inputProps={{ min: 0 }}
                                  sx={{
                                    width: 75,
                                    '& .MuiInputBase-input': {
                                      py: 0.15,
                                      px: 0.15,
                                      fontSize: '0.65rem',
                                      textAlign: 'right'
                                    }
                                  }}
                                />
                              ) : (
                                <span style={{ color: '#9ca3af', fontSize: '0.65rem' }}>
                                  ₹{(item.EventSaleRate || item.Rate || 0).toFixed(2)}
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 600, color: '#1e40af', fontSize: '0.7rem' }}>
                              {isSelected ? (
                                (() => {
                                  const qty = parseFloat(selectedItem.selectedQuantity) || 0;
                                  const days = parseFloat(selectedItem.selectedDays) || 0;
                                  const rate = parseFloat(selectedItem.selectedRate) || 0;
                                  const total = qty * days * rate;
                                  return `₹${total.toFixed(2)}`;
                                })()
                              ) : (
                                <span style={{ color: '#9ca3af' }}>-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {getPaginatedItems().length === 0 && (
                        <tr>
                          <td colSpan="7" style={{ padding: '12px', textAlign: 'center', color: '#6b7280', fontSize: '0.75rem' }}>
                            {getItemsSearchValue ? 'No items found matching your search' : 'No items available'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Box>
              </Box>

              {/* Pagination */}
              {getTotalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      size="small"
                      onClick={() => setCurrentItemsPage(prev => Math.max(1, prev - 1))}
                      disabled={currentItemsPage === 1}
                      sx={{ fontSize: '0.75rem', py: 0.25, px: 1 }}
                    >
                      Previous
                    </Button>
                    <Typography variant="caption" sx={{ mx: 1.5 }}>
                      Page {currentItemsPage} of {getTotalPages}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setCurrentItemsPage(prev => Math.min(getTotalPages, prev + 1))}
                      disabled={currentItemsPage === getTotalPages}
                      sx={{ fontSize: '0.75rem', py: 0.25, px: 1 }}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 1.5, py: 1, borderTop: '1px solid #d1d5db', bgcolor: '#f8fafc', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ color: '#6b7280' }}>
            {selectedItems.length > 0 && `${selectedItems.length} item${selectedItems.length > 1 ? 's' : ''} selected`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={handleCloseGetItemsModal}
              variant="outlined"
              size="small"
              sx={{ fontSize: '0.75rem', py: 0.25, px: 1.5 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddSelectedItems}
              variant="contained"
              size="small"
              disabled={selectedItems.length === 0 || addingItemsLoading}
              sx={{
                bgcolor: '#1e40af',
                '&:hover': { bgcolor: '#1d4ed8' },
                fontSize: '0.75rem',
                py: 0.25,
                px: 1.5
              }}
            >
              {addingItemsLoading ? (
                <>
                  <CircularProgress size={12} sx={{ mr: 0.5 }} />
                  Adding...
                </>
              ) : (
                `Add Selected (${selectedItems.length})`
              )}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* WhatsApp Confirmation Dialog */}
      <Dialog
        open={showWhatsAppDialog}
        onClose={handleSkipWhatsApp}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 1.5,
          px: 2
        }}>
          <WhatsAppIcon sx={{ fontSize: 24 }} />
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            Send to WhatsApp?
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body1" sx={{ color: '#374151', mb: 1 }}>
            Enquiry saved successfully!
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280' }}>
            Do you want to send this enquiry to the customer via WhatsApp?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2, justifyContent: 'center', gap: 1 }}>
          <Button
            onClick={handleSkipWhatsApp}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              px: 3,
              borderColor: '#d1d5db',
              color: '#6b7280',
              '&:hover': {
                borderColor: '#9ca3af',
                backgroundColor: '#f9fafb'
              }
            }}
          >
            No, Skip
          </Button>
          <Button
            onClick={handleSendToWhatsApp}
            variant="contained"
            startIcon={<WhatsAppIcon />}
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              px: 3,
              background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #128C7E 0%, #075E54 100%)'
              }
            }}
          >
            Yes, Send
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
    <Dialog open={!!previewPhoto} onClose={() => setPreviewPhoto(null)} maxWidth="md">
      <DialogContent sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
        {previewPhoto && <img src={previewPhoto} alt="Item" style={{ display: 'block', maxWidth: 'min(80vw, 900px)', maxHeight: '80vh', objectFit: 'contain' }} />}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default EnquiryModal;
